import { ChatbotInputCard } from 'features/chatbot/components/ChatbotInputCard'
import { ChatbotDataContext } from 'features/chatbot/contexts/ChatbotDataLayer'
import { useChatbotSession } from 'features/chatbot/contexts/ChatbotSessionContext'
import { useStartScrollFromEndRef } from 'features/chatbot/hooks/useStartScrollFromEndRef'
import { Sparkles } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { Conversation } from './Conversation'

const ConversationSection = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.75rem;
`

const PinnedScopeBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0 0.75rem 0.25rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid
    color-mix(
      in srgb,
      ${({ theme }) => theme.forecast.border2} 65%,
      transparent
    );
  border-radius: 0.35rem;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.forecast.border2} 12%,
    transparent
  );
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.7rem;

  svg {
    width: 0.8rem;
    height: 0.8rem;
    flex-shrink: 0;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const PinnedScopeLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const PinnedScopeTitle = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 500;
`

/**
 * Conversation area rendered inside the data layer.
 * Auto-derives session name from the first user message.
 */
export const ChatbotContent: React.FC<{ sessionId: string }> = ({
  sessionId,
}) => {
  const scrollFromEndRef = useStartScrollFromEndRef<HTMLDivElement>()
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const { conversation } = useContext(ChatbotDataContext)
  const { deriveSessionName, activeSession } = useChatbotSession()

  useEffect(() => {
    const firstUserMsg = conversation.find((m) => m.type === 'userMessage')
    if (firstUserMsg && firstUserMsg.type === 'userMessage') {
      deriveSessionName(sessionId, firstUserMsg.text)
    }
  }, [conversation, deriveSessionName, sessionId])

  // Persistent indicator showing the chat is bound to a specific
  // case. Only renders when an outer caller (the case page) has
  // pinned `case_id` into the session context. Hidden on all other
  // chat surfaces (process-data, document viewer, etc.).
  const pinnedCaseId =
    typeof activeSession.pinnedContext?.case_id === 'string'
      ? activeSession.pinnedContext.case_id
      : null
  const pinnedCaseTitle =
    typeof activeSession.pinnedContext?.case_title === 'string'
      ? activeSession.pinnedContext.case_title
      : null

  return (
    <>
      <ConversationSection ref={scrollFromEndRef}>
        <Conversation />
      </ConversationSection>
      {pinnedCaseId && (
        <PinnedScopeBanner
          aria-label='Chat scoped to case'
          title={
            pinnedCaseTitle
              ? `Scoped to case: ${pinnedCaseTitle}`
              : `Scoped to case ${pinnedCaseId}`
          }>
          <Sparkles aria-hidden='true' />
          <PinnedScopeLabel>
            Scoped to{' '}
            <PinnedScopeTitle>
              {pinnedCaseTitle ?? pinnedCaseId}
            </PinnedScopeTitle>
          </PinnedScopeLabel>
        </PinnedScopeBanner>
      )}
      <ChatbotInputCard
        helpModalOpen={helpModalOpen}
        setHelpModalOpen={setHelpModalOpen}
      />
    </>
  )
}
